import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, TrendingUp, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Plan } from "@/lib/api";

interface PlanCardProps {
  plan: Plan;
  onViewDetails: (plan: Plan) => void;
  index: number;
}

export default function PlanCard({ plan, onViewDetails, index }: PlanCardProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return "status-success";
      case 'active':
        return "bg-primary/20 text-primary border-primary/30";
      case 'draft':
        return "status-warning";
      default:
        return "bg-secondary/20 text-secondary border-secondary/30";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Extract topics from AI generated content for display
  const topics = plan.aiGeneratedContent?.topics || [];
  const topicColors: Record<string, string> = {
    "Executing": "bg-secondary/10 text-brand-green-dark border-secondary/25",
    "Strategic Thinking": "bg-primary/10 text-primary border-primary/25",
    "Relationship Building": "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25",
    "Influencing": "bg-warning/10 text-warning border-warning/25",
  };

  return (
    <Card
      className="p-4 bg-card backdrop-blur-sm border-border hover:bg-card hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-glow animate-slide-up cursor-pointer sm:p-6"
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={() => onViewDetails(plan)}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className={`min-w-0 break-words text-xl font-bold text-foreground sm:text-2xl ${isRTL ? 'text-right' : 'text-left'}`}>
              {plan.title}
            </h3>
            <Badge className={getStatusVariant(plan.status)}>
              {t(`plans.${plan.status}`) || plan.status}
            </Badge>
          </div>
          
          {/* Topics/Domains */}
          {topics.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {topics.map((topic, idx) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className={topicColors[topic] || "bg-secondary/20 text-secondary border-secondary/30"}
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {topic}
                </Badge>
              ))}
            </div>
          )}
        </div>

      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4 text-primary" />
          <span>{plan.duration} {t('plans.weeks') || 'weeks'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <TrendingUp className="w-4 h-4 text-accent" />
          <span>{formatDate(plan.generatedAt)}</span>
        </div>
      </div>

      {/* Top Strengths */}
      {plan.aiGeneratedContent?.strengths && plan.aiGeneratedContent.strengths.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-medium text-foreground mb-2">
            {t('plans.topStrengths') || 'Top Strengths'}
          </div>
          <div className="flex flex-wrap gap-2">
            {plan.aiGeneratedContent.strengths.slice(0, 5).map((strength, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="bg-muted/50 text-foreground border-border/50"
              >
                {strength}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <Button
        className={
          plan.status === "completed"
            ? "w-full bg-muted hover:bg-muted/80 text-foreground"
            : "w-full bg-primary hover:bg-primary/90 text-primary-foreground"
        }
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(plan);
        }}
      >
        {plan.status === "completed" ? t('plans.reviewPlan') : t('plans.viewDetails')}
      </Button>
    </Card>
  );
}
